<template>
  <UiModal ref="modal" :title="$t('modals.newPassword')" width="550px">
    <div v-if="usuariosStore.loading" class="py-10 flex flex-col items-center justify-center">
      <UiLoader size="40px" class="mb-4" />
      <div>{{ $t('feedback.loading') }}</div>
      <div>{{ $t('feedback.pleaseWait') }}</div>
    </div>

    <div v-else class="flex flex-col gap-4">
      <div class="flex w-full flex-col gap-3">
        <!-- Modo Single -->
        <template v-if="mode === 'single'">
          <div><strong>{{t('column.name')}}:</strong> {{ user?.nomeCompleto }}</div>
          <div v-if="user?.cpf"><strong>{{ t('input.document') }}:</strong> {{ user?.cpf }}</div>
          <div><strong>{{ t('input.email') }}:</strong> {{ user?.email }}</div>
        </template>
        
        <!-- Modo Lote -->
        <template v-else-if="mode === 'batch'">
          <div class="flex items-center gap-2">
            <UiIcon icon="users" size="20" />
            <strong>{{ t('newPassword.batch.selectedUsers') }}:</strong> 
            <span class="px-2 py-1 bg-blue-100 dark:bg-blue-900 rounded text-blue-800 dark:text-blue-200 font-medium">
              {{ userIds.length }}
            </span>
          </div>
          <div class="text-sm text-gray-600 dark:text-gray-400">
            {{ t('newPassword.batch.passwordWillBeChanged') }}
          </div>
        </template>
      </div>
  
      <div class="w-full rounded-md border border-solid border-[#EEEFF1] dark:border-[#79767A]"></div>

      <UiAlert
        v-if="apiError.length"
        type="danger"
        :title="t('newPassword.error.title')"
        class="w-full"
      >
        <div class="font-medium">
          <div v-for="message in apiError" :key="message">{{ message }}</div>
        </div>
      </UiAlert>
      
      <UiForm
        ref="formRef"
        auto-validate
        :disabled="usuariosStore.loading"
        :form-data="form"
        :rules="rules"
      >
        <UiFormControl :label="$t('newPassword.title')" prop="password" class="mb-5 w-full">
          <UiInput v-model="form.password" type="password" togglePassword id="password" :placeholder="$t('placeholder.newPassword')" class="w-full" maxlength="32" @input="apiError = []" />
        </UiFormControl>
        <UiFormControl :label="$t('placeholder.confirmPassword')" prop="confirmPassword" class="w-full">
          <UiInput
            v-model="form.confirmPassword"
            id="confirmPassword"
            togglePassword
            type="password"
            :placeholder="$t('placeholder.confirmPassword')"
            class="w-full"
            maxlength="32"
          />
        </UiFormControl>
  
        <div class="w-full my-8">
          <label>
            <UiSwitch v-model="alterarSenhaNoProximoAcesso"  class="mr-2"/>{{ t('newPassword.batch.changePasswordOnNextAccess') }}
          </label>
        </div>
  
        <div class="my-8">
          <div class="font-semibold mb-4 text-sm">{{t('newPassword.rulesText')}}:</div>
          <ul class="m-0 p-0">
            <li
              v-for="rule in passwordChecklist(t)"
              :key="rule[0]"
              class="text-sm font-medium mb-.5 transition duration-300"
              :class="{
                'text-[#17AC6C]': validation[rule[0]] === 'valid',
                'text-[#FF4980]': validation[rule[0]] === 'invalid'
              }"
            >
              <transition
                mode="out-in"
                enter-active-class="transition transform"
                enter-from-class="opacity-0 scale-0"
                leave-active-class="transition transform"
                leave-to-class="opacity-0 scale-0"
              >
                <UiIcon v-if="validation[rule[0]] === 'valid'" size="20" icon="circle-check" />
                <UiIcon v-else-if="validation[rule[0]] === 'invalid'" size="20" icon="circle-x" />
              </transition>
              {{ rule[1] }}
            </li>
          </ul>
        </div>
  
      </UiForm>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-4">
        <UiButton type="danger" outline class="px-6 py-3 text-sm uppercase outline-[1px]" @click="close">
          {{ t('button.cancel') }}
        </UiButton>
        <UiButton
          type="primary" :disabled="form.password.length === 0 || formRef?.result.$invalid"
          :loading="usuariosStore.loading || busy"
          class="px-6 py-3 text-sm uppercase"
          @click="submit"
        >
          {{ t('button.confirm') }}
        </UiButton>
      </div>
    </template>
  </UiModal>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useUsuariosStore } from '@/stores'
import { toast } from '@/common/util'
import { useI18n } from 'vue-i18n'
import { usersService } from '@/services/usuarios.service'

import UiInput from '@/components/ui/input.vue'
import UiButton from '@/components/ui/button.vue'
import UiForm from '@/components/ui/form.vue'
import UiFormControl from '@/components/ui/formControl.vue'
import UiModal from '@/components/ui/modal.vue'
import UiIcon from '@/components/ui/icon.vue'
import UiSwitch from '@/components/ui/switch.vue'
import UiLoader from '@/components/ui/loader.vue'
import UiAlert from '@/components/ui/alert.vue'

import { createPasswordRules, getPasswordChecklistState, passwordChecklist } from '@/common/password'

import type { UsuarioType } from '@/types/types'

const { t } = useI18n()

const defaultValues = {
  password: '',
  confirmPassword: ''
}

const modal = ref()
const form = reactive({ ...defaultValues })
const user = ref<UsuarioType>()
const userIds = ref<string[]>([])
const mode = ref<'single' | 'batch'>('single')
const alterarSenhaNoProximoAcesso = ref(false)
const formRef = ref()
const busy = ref(false)
const apiError = ref<string[]>([])

const validation = computed(() => getPasswordChecklistState(formRef.value?.result))

const rules = ref(createPasswordRules(t, () => form.password))

const usuariosStore = useUsuariosStore()

const submit = async () => {
  apiError.value = []

  if (!formRef.value.validate()) {
    return toast.error({
      title: t('dialogs.ops'),
      message: t('feedback.formError')
    })
  }

  busy.value = true
  try {
    const result = mode.value === 'single' 
                        ? await usersService.changeIndividualPassword({
                            usuarioId: user.value.id,
                            alterarSenhaNoProximoAcesso: alterarSenhaNoProximoAcesso.value,
                            senha: form.password
                          })

                        : await usersService.changePasswordInBatch({
                            usuarioIds: userIds.value,
                            alterarSenhaNoProximoAcesso: alterarSenhaNoProximoAcesso.value,
                            senha: form.password
                          })

    if (result?.success) {
      const message = mode.value === 'single' 
        ? t('toasts.users.updatedPassword')
        : t('toasts.users.updatedPasswordBatch', { count: userIds.value.length })

      toast.success({ message })

      return close()
    }

    apiError.value = result?.notifications?.length ? result.notifications : [t('newPassword.error.msg')]
  } catch (error) {
    apiError.value = [t('newPassword.error.msg')]
  } finally {
    busy.value = false
  }
}

const open = async (opt: string | Record<string, any> | string[]) => {
  reset()
  await modal.value?.open()

  if (typeof opt === 'string') {
    mode.value = 'single'
    user.value = await usuariosStore.getById(opt)

    if (!user.value?.id) {
      return toast.error({
        message: t('feedback.errorLoadObject', { object: t('labels.user').toLowerCase() }) })
    }
  } else if (Array.isArray(opt)) {
    mode.value = 'batch'
    userIds.value = opt

    if (userIds.value.length === 0) {
      return toast.error({
        message: t('newPassword.batch.noUsersSelected')
      })
    }
  } else if (typeof opt === 'object') {
    // @ts-ignore
    user.value = opt
    mode.value = 'single'
  } 
}

const close = () => {
  reset()
  modal.value?.cancel()
}

const reset = () => {
  Object.assign(form, { ...defaultValues })
  user.value = null
  userIds.value = []
  mode.value = 'single'
  alterarSenhaNoProximoAcesso.value = false
  apiError.value = []
}

defineExpose({
  open,
  close,
  reset
})
</script>